// frontend/src/components/widgets/ClockWidgetConfig.js - OPTIMIZED WITH i18n & PRESETS
import React, { useState, useEffect } from 'react';
import { Clock, X, Save, Calendar, Palette, Layout, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ClockWidget from './ClockWidget';

const ClockWidgetConfig = ({ existingConfig = null, onSave, onCancel }) => {
  const { t } = useTranslation();
  
  // ✅ PRESET DEFINITIONS
  const CLOCK_PRESETS = {
    minimal: {
      format: '24h',
      showDate: false,
      showSeconds: false,
      timezone: 'auto',
      dateFormat: 'long',
      theme: 'modern',
      layout: 'classic',
      showTimezone: false
    },
    standard: {
      format: '24h',
      showDate: true,
      showSeconds: true,
      timezone: 'auto',
      dateFormat: 'long',
      theme: 'modern',
      layout: 'dashboard',
      showTimezone: false
    },
    full: {
      format: '24h',
      showDate: true,
      showSeconds: true,
      timezone: 'Europe/Zurich',
      dateFormat: 'long',
      theme: 'modern',
      layout: 'dashboard',
      showTimezone: true
    }
  };
  
  // Default configuration
  const [config, setConfig] = useState({
    name: existingConfig?.name || '',
    config: {
      format: existingConfig?.config?.format || '24h',
      showDate: existingConfig?.config?.showDate ?? true,
      showSeconds: existingConfig?.config?.showSeconds ?? true,
      timezone: existingConfig?.config?.timezone || 'auto',
      dateFormat: existingConfig?.config?.dateFormat || 'long',
      theme: existingConfig?.config?.theme || 'modern',
      layout: existingConfig?.config?.layout || 'dashboard',
      showTimezone: existingConfig?.config?.showTimezone ?? false
    }
  });

  const [errors, setErrors] = useState({});
  const [selectedPreset, setSelectedPreset] = useState('custom');

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value
      }
    }));
    setSelectedPreset('custom'); // Reset to custom when manually changing
  };

  const handleNameChange = (value) => {
    setConfig(prev => ({
      ...prev,
      name: value
    }));
  };

  // ✅ PRESET HANDLER
  const handlePresetChange = (presetKey) => {
    if (presetKey === 'custom') {
      setSelectedPreset('custom');
      return;
    }

    const preset = CLOCK_PRESETS[presetKey];
    if (preset) {
      setConfig(prev => ({
        ...prev,
        config: { ...preset }
      }));
      setSelectedPreset(presetKey);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!config.name.trim()) {
      newErrors.name = t('clock.errors.nameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      console.log('📤 ClockWidgetConfig submitting:', config);
      onSave(config);
    }
  };

  // Format options
  const formatOptions = [
    { value: '24h', label: t('clock.format24h') },
    { value: '12h', label: t('clock.format12h') }
  ];

  const dateFormatOptions = [
    { value: 'long', label: t('clock.dateFormatLong') },
    { value: 'short', label: t('clock.dateFormatShort') },
    { value: 'numeric', label: t('clock.dateFormatNumeric') },
    { value: 'iso', label: t('clock.dateFormatIso') }
  ];

  const timezoneOptions = [
    { value: 'auto', label: t('clock.timezoneAuto') },
    { value: 'Europe/Zurich', label: t('clock.timezoneZurich') },
    { value: 'Europe/Berlin', label: t('clock.timezoneBerlin') },
    { value: 'Europe/Vienna', label: t('clock.timezoneVienna') },
    { value: 'Europe/Paris', label: t('clock.timezoneParis') },
    { value: 'Europe/London', label: t('clock.timezoneLondon') },
    { value: 'America/New_York', label: t('clock.timezoneNewYork') },
    { value: 'America/Los_Angeles', label: t('clock.timezoneLosAngeles') },
    { value: 'Asia/Tokyo', label: t('clock.timezoneTokyo') },
    { value: 'Australia/Sydney', label: t('clock.timezoneSydney') }
  ];

  const themeOptions = [
    { value: 'modern', label: t('clock.themeModern') },
    { value: 'classic', label: t('clock.themeClassic') },
    { value: 'minimal', label: t('clock.themeMinimal') },
    { value: 'dark', label: t('clock.themeDark') },
    { value: 'weather', label: t('clock.themeWeather') }
  ];

  const layoutOptions = [
    { value: 'dashboard', label: t('clock.layoutDashboard') },
    { value: 'classic', label: t('clock.layoutClassic') }
  ];

  // ✅ PRESET OPTIONS
  const presetOptions = [
    { value: 'custom', label: t('clock.presetCustom') },
    { value: 'minimal', label: t('clock.presetMinimal'), desc: t('clock.presetMinimalDesc') },
    { value: 'standard', label: t('clock.presetStandard'), desc: t('clock.presetStandardDesc') },
    { value: 'full', label: t('clock.presetFull'), desc: t('clock.presetFullDesc') }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
            <Clock size={24} className="text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {existingConfig ? t('clock.editClockWidget') : t('clock.clockWidgetConfiguration')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('clock.setupClockDisplay')}
            </p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-6">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Left Column: Configuration */}
            <div className="space-y-6">
              
              {/* Widget Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('widgets.widgetName')} *
                </label>
                <input
                  type="text"
                  value={config.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`w-full px-4 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100`}
                  placeholder={t('clock.clockWidgetNamePlaceholder')}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* ✅ PRESET SELECTOR */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Sparkles size={20} />
                  {t('clock.presets')}
                </h4>
                <div className="space-y-2">
                  {presetOptions.map(preset => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => handlePresetChange(preset.value)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        selectedPreset === preset.value
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                      }`}
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {preset.label}
                      </div>
                      {preset.desc && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {preset.desc}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Basic Settings */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Clock size={20} />
                  {t('clock.basicSettings')}
                </h4>

                {/* Time Format */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('clock.timeFormat')}
                  </label>
                  <select
                    value={config.config.format}
                    onChange={(e) => handleInputChange('format', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    {formatOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Timezone */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('clock.timezone')}
                  </label>
                  <select
                    value={config.config.timezone}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    {timezoneOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Show Seconds */}
                <div className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.config.showSeconds}
                      onChange={(e) => handleInputChange('showSeconds', e.target.checked)}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('clock.showSeconds')}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 ml-8 mt-1">
                    {t('clock.showSecondsHelp')}
                  </p>
                </div>
              </div>

              {/* Date Settings */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Calendar size={20} />
                  {t('clock.dateSettings')}
                </h4>

                {/* Show Date */}
                <div className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.config.showDate}
                      onChange={(e) => handleInputChange('showDate', e.target.checked)}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('clock.showDate')}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 ml-8 mt-1">
                    {t('clock.showDateHelp')}
                  </p>
                </div>

                {/* Date Format */}
                {config.config.showDate && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('clock.dateFormat')}
                    </label>
                    <select
                      value={config.config.dateFormat}
                      onChange={(e) => handleInputChange('dateFormat', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      {dateFormatOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Appearance Settings */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Palette size={20} />
                  {t('clock.appearance')}
                </h4>

                {/* Layout Style */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Layout size={16} className="inline mr-2" />
                    {t('clock.layoutStyle')}
                  </label>
                  <select
                    value={config.config.layout}
                    onChange={(e) => handleInputChange('layout', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    {layoutOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('clock.layoutDashboardHelp')}
                  </p>
                </div>

                {/* Theme */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('clock.theme')}
                  </label>
                  <select
                    value={config.config.theme}
                    onChange={(e) => handleInputChange('theme', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    {themeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Show Timezone Label */}
                {config.config.layout === 'dashboard' && config.config.timezone !== 'auto' && (
                  <div className="mb-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config.config.showTimezone}
                        onChange={(e) => handleInputChange('showTimezone', e.target.checked)}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('clock.showTimezone')}
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 ml-8 mt-1">
                      {t('clock.showTimezoneHelp')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Live Preview */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('clock.livePreview')}
              </h4>
              <div 
                className="bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden sticky top-4"
                style={{ height: '600px' }}
              >
                <ClockWidget config={config.config} screenSize="HD" />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Save size={20} />
            {existingConfig ? t('common.update') : t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClockWidgetConfig;